import { useForm } from "antd/lib/form/Form";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Drawer, Row, Space } from "antd";
import { BiFilterAlt } from "react-icons/bi";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import moment from "moment";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import ContentHeaderComponent from "../../shared/components/content-header/content-header.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import { getMilestonesList } from "../../core/store/reducers/milestone.slice";
import { RootState } from "../../shared/types/common";
import { formatCost1 } from "../../shared/utils/utils";
import { Client } from "../../core/api-client";
import {
  API_GET_ALL_PROJECT_LIST,
  API_GET_ALL_STAFF_LIST,
} from "../../shared/routes/private.routes";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import CreateEditMilestoneComponent from "./components/create-edit-milestone.component";

const INVOICE_STATUS_OPTIONS = [
  { value: "PENDING", label: "PENDING" },
  { value: "RAISED", label: "RAISED" },
  { value: "PAID", label: "PAID" },
];

const ManageMilestonesPage: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const {
    loadingMilestonesList,
    milestonesList,
    pagination,
  } = useSelector((state: RootState) => state.milestone);

  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState<any>({});
  const [sorter, setSorter] = useState({ sort_by: "order", sort_type: "ASC" });
  const [listData, setListData] = useState<any[]>([]);
  const [staffOptions, setStaffOptions] = useState<{ value: string; label: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ value: string; label: string }[]>([]);
  const [activityLogVisible, setActivityLogVisible] = useState(false);
  const [selectedMilestoneForLog, setSelectedMilestoneForLog] = useState<any>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<any>(null);

  const loadList = useCallback(
    (page: number | null = null) => {
      const body: any = {
        page: page ?? pagination?.page ?? 1,
        limit: pagination?.limit ?? TABLE_ROWS_COUNT,
        sort_by: sorter.sort_by || "order",
        sort_type: sorter.sort_type || "ASC",
      };
      Object.keys(filters).forEach((key) => {
        if (filters[key] !== undefined && filters[key] !== "" && filters[key] !== null) {
          body[key] = filters[key];
        }
      });
      dispatch(getMilestonesList({ body }));
    },
    [dispatch, filters, pagination?.limit, pagination?.page, sorter]
  );

  useEffect(() => {
    loadList();
  }, [filters, sorter]);

  useEffect(() => {
    if (milestonesList?.length) {
      const sorted = [...milestonesList].sort((a, b) => {
        const pA = (a.projectName || "").toLowerCase();
        const pB = (b.projectName || "").toLowerCase();
        if (pA !== pB) return pA.localeCompare(pB);
        return (a.order ?? 0) - (b.order ?? 0);
      });
      setListData(sorted.map((r: any, i: number) => ({ key: i, ...r })));
    } else {
      setListData([]);
    }
  }, [milestonesList]);

  useEffect(() => {
    Client.get(API_GET_ALL_STAFF_LIST).then((res: any) => {
      const list = res?.data?.data || [];
      setStaffOptions(
        list.map((s: any) => ({
          value: s.staffId,
          label: s.staffName || s.employeeId || s.staffId,
        }))
      );
    });
    Client.get(API_GET_ALL_PROJECT_LIST).then((res: any) => {
      const list = res?.data?.data?.data || res?.data?.data || [];
      setProjectOptions(
        list.map((p: any) => ({
          value: p.projectId,
          label: p?.l2ProjectName + " (" + p?.sowNumber + ")",
        }))
      );
    });
  }, []);

  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };

  const handleViewActivityLog = (record: any) => {
    setSelectedMilestoneForLog(record);
    setActivityLogVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingMilestone(record);
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    loadList(pagination?.page ?? 1);
    setEditModalVisible(false);
    setEditingMilestone(null);
  };

  const columns = useMemo(
    () => [
      { title: "SoW#", dataIndex: "sowNumber", key: "sowNumber", width: 100, ellipsis: true },
      { title: "PO Number", dataIndex: "poNumber", key: "poNumber", width: 110, ellipsis: true },
      { title: "Project", dataIndex: "projectName", key: "projectName", width: 160, ellipsis: true },
      { title: "SPOC", dataIndex: "spocName", key: "spocName", width: 120, ellipsis: true },
      { title: "Milestone", dataIndex: "name", key: "name", width: 160, ellipsis: true },
      {
        title: "%Age",
        dataIndex: "percentage",
        key: "percentage",
        width: 70,
        render: (v: any) => (v != null ? Number(v) : "-"),
      },
      {
        title: "PO Currency",
        dataIndex: "poCurrencyName",
        key: "poCurrencyName",
        width: 100,
        render: (v: any) => v ?? "-",
      },
      {
        title: "PO Value",
        dataIndex: "poValue",
        key: "poValue",
        width: 110,
        render: (v: any, record: any) =>
          v != null ? formatCost1(Number(v), record.poCurrencyName || "") : "-",
      },
      {
        title: "Milestone Value",
        key: "milestoneValue",
        width: 120,
        render: (_: any, record: any) => {
          const pct = record.percentage != null ? Number(record.percentage) : null;
          const poVal = record.poValue != null ? Number(record.poValue) : null;
          if (pct != null && poVal != null) {
            const val = (poVal * pct) / 100;
            return formatCost1(val, record.poCurrencyName || "");
          }
          return "-";
        },
      },
      {
        title: "Est Billing Date",
        dataIndex: "estimatedBillingDate",
        key: "estimatedBillingDate",
        width: 120,
        render: (v: string) => (v ? moment(v).format("DD-MMM-YYYY") : "-"),
      },
      {
        title: "Invoice Status",
        dataIndex: "invoiceStatus",
        key: "invoiceStatus",
        width: 110,
      },
      {
        title: "Invoice Date",
        dataIndex: "invoiceDate",
        key: "invoiceDate",
        width: 110,
        render: (v: string) => (v ? moment(v).format("DD-MMM-YYYY") : "-"),
      },
      {
        title: "Due Date",
        dataIndex: "invoiceDueDate",
        key: "invoiceDueDate",
        width: 110,
        render: (v: string) => (v ? moment(v).format("DD-MMM-YYYY") : "-"),
      },
      { title: "Invoice No.", dataIndex: "invoiceNumber", key: "invoiceNumber", width: 110 },
      {
        title: "Payment received",
        dataIndex: "paymentReceived",
        key: "paymentReceived",
        width: 110,
        render: (v: boolean) => (v ? "Yes" : "No"),
      },
      {
        title: "Payment Receipt date",
        dataIndex: "paymentReceivedDate",
        key: "paymentReceivedDate",
        width: 130,
        render: (v: string) => (v ? moment(v).format("DD-MMM-YYYY") : "-"),
      },
      {
        title: "Actions",
        key: "actions",
        width: 100,
        fixed: "right" as const,
        render: (_: any, record: any) => (
          <Space>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewActivityLog(record)}
              title="Activity Log"
            />
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              title="Edit"
            />
          </Space>
        ),
      },
    ],
    []
  );

  const formProps = {
    form,
    name: "milestones-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "name",
        label: "",
        placeholder: "Milestone name",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "projectId",
        label: "",
        placeholder: "Project",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        options: projectOptions,
      },
      {
        name: "spoc",
        label: "",
        placeholder: "SPOC",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        options: staffOptions,
      },
      {
        name: "invoiceStatus",
        label: "",
        placeholder: "Invoice Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        allowClear: true,
        options: INVOICE_STATUS_OPTIONS,
      },
      {
        name: "paymentReceived",
        label: "",
        placeholder: "Payment received",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        allowClear: true,
        options: [
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ],
      },
    ],
    initialValues: {},
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
  };

  return (
    <>
      <div
        className="mx-6 mt-2 bg-primary-white-blueish border-primary-gray-02"
        style={{ borderBottom: "none!important" }}
      >
        <Row>
          <Col span={24}>
            <ContentHeaderComponent
              title="Milestones"
              subtitle="View and manage milestones across projects"
              buttons={
                <Button
                  shape="circle"
                  type="primary"
                  icon={<BiFilterAlt />}
                  onClick={() => setFilterVisible(!filterVisible)}
                />
              }
            />
          </Col>
        </Row>
        {filterVisible && (
          <Row className="bg-filter">
            <Col span={24} className="py-1">
              <div className="px-3 table-filter-form">
                <GenericFormComponent {...formProps} />
              </div>
            </Col>
          </Row>
        )}
      </div>

      <DataTableComponent
        pagination
        loading={loadingMilestonesList}
        dataSource={listData}
        columns={columns}
        defaultPage={0}
        currentPage={pagination?.page ?? 1}
        totalRows={pagination?.total ?? 0}
        onPageChange={onPageChange}
        tableWidth="2400px"
      />

      {activityLogVisible && selectedMilestoneForLog && (
        <Drawer
          open={activityLogVisible}
          onClose={() => {
            setActivityLogVisible(false);
            setSelectedMilestoneForLog(null);
          }}
          title={`Activity Log - ${selectedMilestoneForLog.name}`}
          width={480}
        >
          <ActivityLogComponent
            id={selectedMilestoneForLog.milestoneId}
            record={selectedMilestoneForLog}
            module="Milestone"
          />
        </Drawer>
      )}

      {editModalVisible && editingMilestone && (
        <CreateEditMilestoneComponent
          visible={editModalVisible}
          onClose={() => {
            setEditModalVisible(false);
            setEditingMilestone(null);
          }}
          projectId={editingMilestone.projectId}
          selectedMilestone={editingMilestone}
          staffOptions={staffOptions}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  );
};

export default ManageMilestonesPage;
